import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { useAuth } from '../components/AuthContext';
import { useAsyncActionMap } from '../hooks/useAsyncAction';

import { useToast } from '../context/ToastContext';
import { useConfirmDialog } from '../context/ConfirmDialogContext';
import { formatDate } from '../utils/format';
import DataTable from '../components/DataTable';
const API_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';

function CompaniesPage() {
  const toast = useToast();
  const { confirm } = useConfirmDialog();
  const { t } = useTranslation();
  const { hasPermission } = useAuth();
  const { executeWithId, isLoadingId } = useAsyncActionMap();
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingCompany, setEditingCompany] = useState(null);
  const [formData, setFormData] = useState({ name: '', status: 'active' });
  const [submitting, setSubmitting] = useState(false);

  const fetchCompanies = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/companies`);
      setCompanies(response.data);
    } catch (error) {
      console.error('Failed to fetch companies:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCompanies();
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    const permission = editingCompany ? 'system.companies.edit' : 'system.companies.create';
    if (!hasPermission(permission)) {
      toast.error(t('common.errors.permissionDenied'));
      return;
    }

    if (submitting) return;
    setSubmitting(true);

    try {
      if (editingCompany) {
        await axios.put(`${API_URL}/api/companies/${editingCompany.id}`, formData);
      } else {
        await axios.post(`${API_URL}/api/companies`, formData);
      }

      setShowModal(false);
      setEditingCompany(null);
      setFormData({ name: '', status: 'active' });
      fetchCompanies();
    } catch (error) {
      toast.error(t('companies.saveFailed'));
    } finally {
      setSubmitting(false);
    }
  };

  const handleEdit = (company) => {
    if (!hasPermission('system.companies.edit')) {
      toast.error(t('common.errors.permissionDenied'));
      return;
    }
    setEditingCompany(company);
    setFormData({ name: company.name, status: company.status });
    setShowModal(true);
  };

  const handleDelete = async (company) => {
    const { id } = company;
    if (isLoadingId(`delete-${id}`)) return;
    if (!hasPermission('system.companies.delete')) {
      toast.error(t('common.errors.permissionDenied'));
      return;
    }
    const confirmed = await confirm({
      title: t('sharedui.adminConfirm.deleteCompanyTitle'),
      message: t('sharedui.adminConfirm.deleteCompany', { name: company.name }),
      confirmText: t('sharedui.adminConfirm.deleteButton'),
      cancelText: t('sharedui.adminConfirm.cancelButton'),
      type: 'danger',
    });
    if (!confirmed) return;

    await executeWithId(`delete-${id}`, async () => {
      try {
        await axios.delete(`${API_URL}/api/companies/${id}`);
        fetchCompanies();
      } catch (error) {
        toast.error(t('companies.deleteFailed'));
      }
    });
  };

  const handleSuspend = async (company) => {
    const { id } = company;
    if (isLoadingId(`suspend-${id}`)) return;
    if (!hasPermission('system.companies.edit')) {
      toast.error(t('common.errors.permissionDenied'));
      return;
    }
    // Suspending a tenant is disruptive: always ask first, naming the company.
    const confirmed = await confirm({
      title: t('sharedui.adminConfirm.suspendCompanyTitle'),
      message: t('sharedui.adminConfirm.suspendCompany', { name: company.name }),
      confirmText: t('sharedui.adminConfirm.suspendButton'),
      cancelText: t('sharedui.adminConfirm.cancelButton'),
      type: 'danger',
    });
    if (!confirmed) return;

    await executeWithId(`suspend-${id}`, async () => {
      try {
        await axios.put(`${API_URL}/api/companies/${id}/suspend`);
        fetchCompanies();
      } catch (error) {
        toast.error(t('companies.suspendFailed'));
      }
    });
  };

  const handleActivate = async (id) => {
    if (isLoadingId(`activate-${id}`)) return;
    if (!hasPermission('system.companies.edit')) {
      toast.error(t('common.errors.permissionDenied'));
      return;
    }

    await executeWithId(`activate-${id}`, async () => {
      try {
        await axios.put(`${API_URL}/api/companies/${id}/activate`);
        fetchCompanies();
      } catch (error) {
        toast.error(t('companies.activateFailed'));
      }
    });
  };

  return (
    <div data-testid="companies-page" className="space-y-6 fade-in">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="page-title text-3xl mb-2">{t('companies.title')}</h1>
          <p className="text-gray-600 dark:text-gray-400">{t('companies.subtitle')}</p>
        </div>
        <button
          data-testid="add-company-button"
          onClick={() => {
            setEditingCompany(null);
            setFormData({ name: '', status: 'active' });
            setShowModal(true);
          }}
          className="bg-primary-600 hover:bg-primary-700 text-white font-semibold py-2 px-6 rounded-lg transition"
        >
          + {t('companies.addCompany')}
        </button>
      </div>

      <DataTable
        id="companies"
        rows={companies}
        loading={loading}
        exportName="sirketler"
        defaultSort={{ key: 'name', order: 'asc' }}
        emptyText={t('companies.empty')}
        columns={[
          { key: 'name', header: t('common.name'), sortable: true,
            render: (company) => <span className="font-semibold text-gray-900 dark:text-gray-100">{company.name}</span> },
          { key: 'status', header: t('common.status'), sortable: true,
            value: (company) => t(`companies.status.${company.status}`, { defaultValue: company.status }),
            render: (company) => (
              <span
                data-testid={`company-status-${company.id}`}
                className={`px-3 py-1 rounded-full text-xs font-medium ${
                  company.status === 'active'
                    ? 'bg-green-100 text-green-800 dark:bg-green-900/40 dark:text-green-300'
                    : 'bg-red-100 text-red-800 dark:bg-red-900/40 dark:text-red-300'
                }`}
              >
                {t(`companies.status.${company.status}`, { defaultValue: company.status })}
              </span>
            ) },
          { key: 'created_at', header: t('common.createdAt'), sortable: true,
            render: (company) => formatDate(company.created_at) },
        ]}
        rowActions={(company) => (
          <span className="inline-flex flex-wrap justify-end gap-2">
            <button
              data-testid={`edit-company-${company.id}`}
              onClick={() => handleEdit(company)}
              className="bg-primary-50 hover:bg-primary-100 text-primary-700 font-medium py-1.5 px-3 rounded-lg transition text-sm"
            >
              {t('common.edit')}
            </button>

            {company.status === 'active' ? (
              <button
                data-testid={`suspend-company-${company.id}`}
                onClick={() => handleSuspend(company)}
                disabled={isLoadingId(`suspend-${company.id}`)}
                className="bg-yellow-50 hover:bg-yellow-100 text-yellow-700 font-medium py-1.5 px-3 rounded-lg transition text-sm disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isLoadingId(`suspend-${company.id}`) ? t('companies.processing') : t('companies.suspend')}
              </button>
            ) : (
              <button
                data-testid={`activate-company-${company.id}`}
                onClick={() => handleActivate(company.id)}
                disabled={isLoadingId(`activate-${company.id}`)}
                className="bg-green-50 hover:bg-green-100 text-green-700 font-medium py-1.5 px-3 rounded-lg transition text-sm disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isLoadingId(`activate-${company.id}`) ? t('companies.processing') : t('companies.activate')}
              </button>
            )}

            <button
              data-testid={`delete-company-${company.id}`}
              onClick={() => handleDelete(company)}
              disabled={isLoadingId(`delete-${company.id}`)}
              className="bg-red-50 hover:bg-red-100 text-red-700 font-medium py-1.5 px-3 rounded-lg transition text-sm disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isLoadingId(`delete-${company.id}`) ? t('companies.deleting') : t('common.delete')}
            </button>
          </span>
        )}
      />

      {/* Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-start md:items-center justify-center p-2 sm:p-4 z-50 overflow-y-auto" onClick={(e) => { if (e.target === e.currentTarget) setShowModal(false); }}>
          <div data-testid="company-modal" className="bg-white dark:bg-gray-800 rounded-xl shadow-2xl max-w-md w-full p-4 sm:p-6 my-4 sm:my-8 max-h-[95vh] sm:max-h-[90vh] overflow-y-auto fade-in">
            <div className="flex justify-between items-center mb-4 sm:mb-6">
              <h2 className="text-xl sm:page-title text-2xl">
                {editingCompany ? t('companies.editCompany') : t('companies.addNewCompany')}
              </h2>
              <button
                type="button"
                onClick={() => {
                  setShowModal(false);
                  setEditingCompany(null);
                  setFormData({ name: '', status: 'active' });
                }}
                className="text-gray-400 hover:text-gray-600 dark:text-surface-400 transition"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            <form onSubmit={handleSubmit} className="space-y-3 sm:space-y-4">
              <div>
                <label className="form-label">
                  {t('companies.nameRequired')}
                </label>
                <input
                  type="text"
                  data-testid="company-name-input"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  required
                  className="w-full px-3 sm:px-4 py-2 sm:py-3 text-sm sm:text-base border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent outline-none"
                  placeholder={t('companies.namePlaceholder')}
                />
              </div>

              <div>
                <label className="form-label">
                  {t('common.status')}
                </label>
                <select
                  data-testid="company-status-select"
                  value={formData.status}
                  onChange={(e) => setFormData({ ...formData, status: e.target.value })}
                  className="w-full px-3 sm:px-4 py-2 sm:py-3 text-sm sm:text-base border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent outline-none"
                >
                  <option value="active">{t('companies.status.active')}</option>
                  <option value="suspended">{t('companies.status.suspended')}</option>
                </select>
              </div>

              <div className="flex gap-2 sm:gap-3 pt-4">
                <button
                  type="button"
                  data-testid="cancel-company-button"
                  onClick={() => {
                    setShowModal(false);
                    setEditingCompany(null);
                    setFormData({ name: '', status: 'active' });
                  }}
                  disabled={submitting}
                  className="flex-1 bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 text-gray-700 dark:text-gray-300 font-semibold py-2 sm:py-3 px-3 sm:px-4 text-sm sm:text-base rounded-lg transition disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {t('common.cancel')}
                </button>
                <button
                  type="submit"
                  data-testid="save-company-button"
                  disabled={submitting}
                  className={`flex-1 font-semibold py-2 sm:py-3 px-3 sm:px-4 text-sm sm:text-base rounded-lg transition ${
                    submitting
                      ? 'bg-gray-400 text-gray-200 cursor-not-allowed'
                      : 'bg-primary-600 hover:bg-primary-700 text-white'
                  }`}
                >
                  {submitting ? t('companies.saving') : (editingCompany ? t('common.update') : t('common.create'))}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

export default CompaniesPage;
